import { JSX } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CoffeeIcon from '@mui/icons-material/Coffee';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import type { InterviewState } from 'src/redux/slices/Interview/types';
import InterviewFooter from '../components/InterviewFooter';

export default function NothingToDoScreen(): JSX.Element {
  const navigate = useNavigate();
  const interview = useSelector(
    (state: { interview: InterviewState }) => state.interview
  );

  const handlePrimary = (): void => {
    navigate(interview.backPath ?? '/renter');
  };

  const handleViewOnFile = (): void => {
    if (interview.surveyId) {
      const memberSeg = interview.onBehalfOf
        ? `/members/${interview.onBehalfOf}`
        : '';
      navigate(`/renter/survey/${interview.surveyId}${memberSeg}`);
      return;
    }
    navigate('/renter/data-vault');
  };

  return (
    <Box
      data-testid="nothing-to-do-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
    >
      {/* Body — vertically centered */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          flex: 1,
          gap: 3,
          px: { xs: 3, md: 4 },
          py: { xs: 5, md: 5 },
        }}
      >
        <Box
          sx={{
            width: { xs: 88, md: 96 },
            height: { xs: 88, md: 96 },
            borderRadius: '50%',
            bgcolor: 'primary.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CoffeeIcon
            sx={{ fontSize: { xs: 40, md: 44 }, color: 'primary.main' }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: { xs: 24, md: 28 },
            fontWeight: 700,
            lineHeight: 1.2,
            maxWidth: { xs: 300, md: 440 },
          }}
        >
          Nothing else to do right now
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 15, md: 16 },
            color: 'text.secondary',
            lineHeight: 1.5,
            maxWidth: { xs: 300, md: 440 },
          }}
        >
          We are reviewing your application and will let you know if we need
          anything else from you.
        </Typography>
        {/* Desktop-only inline action buttons */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: 2,
            justifyContent: 'center',
            mt: 1,
          }}
        >
          <Button
            onClick={handleViewOnFile}
            data-testid="view-on-file-button-inline"
            sx={{
              height: 44,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.300',
              color: 'text.secondary',
              fontSize: 14,
              fontWeight: 500,
              px: 2.5,
              textTransform: 'none',
            }}
          >
            View what we have on file
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrimary}
            data-testid="go-home-button-inline"
            sx={{
              height: 44,
              borderRadius: '12px',
              fontSize: 16,
              fontWeight: 600,
              px: 3,
              textTransform: 'none',
            }}
          >
            {interview.backPath ? 'Return to section' : 'Go to home page'}
          </Button>
        </Box>
      </Box>

      {/* Mobile-only footer */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <InterviewFooter
          primaryLabel={
            interview.backPath ? 'Return to section' : 'Go to home page'
          }
          secondaryLabel="View what we have on file"
          onPrimary={handlePrimary}
          onSecondary={handleViewOnFile}
          primaryTestId="go-home-button"
          secondaryTestId="view-on-file-button"
        />
      </Box>
    </Box>
  );
}
